import type { AnyObject } from 'yup';

import { WebAppErr } from './WebAppErr';
import { toast } from './toast';

interface ArgType {
  tagName?: string;
  showAlert?: boolean;
  fallbackMsg?: string;
  additionalArgs?: AnyObject;
}

export const flattenObject = (objectToFlatten: AnyObject, prefix = '') => {
  const flattenedObject: AnyObject = {};

  Object.keys(objectToFlatten).forEach((currentKey) => {
    const currentValue = objectToFlatten[currentKey];
    const newKey = prefix ? `${prefix}.${currentKey}` : currentKey;
    if (typeof currentValue === 'object' && currentValue !== null) {
      Object.assign(flattenedObject, flattenObject(currentValue, newKey));
    } else {
      flattenedObject[newKey] = currentValue;
    }
  });

  return flattenedObject;
};

/**
 * A generic error handler for the application. This function is called from anywhere.
 * The function checks the type of error and calls the appropriate handler.
 * Basic ideas of this function are:
 * 1. Report the error to Sentry with more richful context
 * 2. Show the generic toast message to the user (configurable)
 *
 * @param {error} err
 * @param {object} argument { showAlert, feature, additionalInfo, fallbackMsg } see below:
 *  - @param {boolean} argument.showAlert - Show the alert to the user or not
 *  - @param {string} argument.feature - Feature name where the error occurred
 *  - @param {object} argument.additionalInfo - Additional information to be sent to Sentry. (Redux state or any payload)
 *  - @param {string} argument.fallbackMsg - Fallback message to show to the user
 */
export const handleErrors = (err: Error, arg?: ArgType) => {
  const {
    showAlert = true,
    fallbackMsg = '',
    additionalArgs = {},
    tagName = '',
  } = arg || {
    showAlert: true,
    fallbackMsg: '',
    additionalArgs: {},
    tagName: '',
  };

   
  const tempMsg = {
    msg: `context: ${err.message}, additionalMsg: ${fallbackMsg} `,
  };

  if (err instanceof WebAppErr && err.isWebAppErr) {
    if (showAlert) {
      toast({
        id: 'WEB-APP-ERROR',
        title: 'Web App Error',
      });
    }
    const finalArg = { ...err.args, ...additionalArgs };
    // FLEETPANDA-TODO: this needs to be send to sentry or any other error tracking service
    console.error(err, tempMsg, flattenObject(finalArg), tagName);
    return;
  }

  if (showAlert) {
    toast({
      title: fallbackMsg || err.message,
    });
  }
  // FLEETPANDA-TODO: this needs to be send to sentry or any other error tracking service
  console.error(err, tempMsg, flattenObject(additionalArgs), tagName);
};
